import { XIcon } from "@heroicons/react/solid";
import { FieldInputProps, FormikProps } from "formik";
import React, { useEffect, useMemo } from "react";
import { DatePicker } from "react-widgets";
import classNames from "../../utils/classNames";
import { useForm } from "./FormProvider";

interface Props {
  className?: {
    wrapper: string;
    label: string;
    input: string;
  };
  placeholder?: string;
  id?: string;
  defaultValue?: Date | null;
  min?: Date;
  max?: Date;
  field: FieldInputProps<any>;
  form: FormikProps<any>;
  label?: string;
  helperText?: string;
  includeTime?: boolean;
  disabled?: boolean;
  withoutErrorLabel?: boolean;
}

const DateInput: React.FC<Props> = ({
  className,
  id,
  field,
  form: { touched, errors, setFieldTouched, setFieldValue, isValidating },
  label,
  helperText,
  includeTime = false,
  withoutErrorLabel = false,
  placeholder,
  defaultValue,
  disabled = false,
  min,
  max,
  ...otherProps
}) => {
  const { isFirstErrorField } = useForm();

  const firstErrorField = isFirstErrorField(errors, field.name);
  const error = useMemo(
    () => (touched[field.name] || firstErrorField) && errors[field.name],
    [errors, field.name, firstErrorField, touched]
  );
  const hasError = useMemo(
    () => !isValidating && !!error,
    [error, isValidating]
  );

  const dateInputProps = useMemo(
    () => ({
      id: id || field.name,
      invalid: hasError ? "true" : null,
      ...field
    }),
    [field, hasError, id]
  );

  useEffect(() => {
    const input = document.getElementById(dateInputProps.id);

    input?.getElementsByTagName("input")[0]?.setAttribute("disabled", "true");
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      id={dateInputProps.id}
      className={classNames("mt-6", className?.wrapper)}
    >
      {label && (
        <label
          htmlFor={id || field.name}
          className={classNames(
            "block mb-1 text-sm font-medium text-gray-900 dark:text-white",
            className?.label
          )}
        >
          {label}
        </label>
      )}

      <div className="flex items-center relative">
        <DatePicker
          className={classNames(
            "w-full",
            className?.input,
            (disabled === undefined ? false : disabled) &&
              "pointer-events-none opacity-50",
            dateInputProps.invalid ? "InvalidDate" : undefined
          )}
          onBlur={() => setFieldTouched(field.name, true)}
          value={field.value}
          defaultValue={defaultValue}
          disabled={disabled}
          placeholder={placeholder}
          min={min}
          max={max}
          includeTime={includeTime}
          onChange={(date: Date | null | undefined, rawValue: string) => {
            if (date && !isNaN(date.getTime())) {
              setFieldValue(field.name, date, !dateInputProps.invalid);

              return;
            }

            setFieldValue(field.name, undefined, !dateInputProps.invalid);
          }}
          {...otherProps}
        />
        {field.value && (
          <XIcon
            className={classNames(
              "w-5 h-5 absolute right-0 mr-10 transition-color ease-in duration-100 dark:text-gray-400",
              disabled
                ? "opacity-50"
                : "cursor-pointer hover:text-gray-500 dark:hover:text-white"
            )}
            onClick={() => {
              if (disabled) return;

              setFieldValue(field.name, undefined, !dateInputProps.invalid);
            }}
          />
        )}
      </div>

      {!withoutErrorLabel && (helperText || hasError) && (
        <p
          className={classNames(
            "field-error mt-2 text-sm text-gray-600",
            hasError && "text-red-600"
          )}
        >
          {hasError ? error : helperText}
        </p>
      )}
    </div>
  );
};

export default DateInput;
